// Shared variable substitution for template previews.
// Mirrors the backend engine in /app/backend/reachly/personalize.py

const VAR_RE = /\{{1,2}([a-zA-Z_][a-zA-Z0-9_]*)(?:[:|]((?:[^{}])*))?\}{1,2}/g;

const ALIASES = {
  name: 'first_name', firstname: 'first_name', lastname: 'last_name',
  title: 'job_title', role: 'job_title', jobtitle: 'job_title',
  organization: 'company', org: 'company',
};

function getField(contact, name) {
  if (!contact) return '';
  let key = name.toLowerCase();
  key = ALIASES[key] || key;
  const v = contact[key] ?? contact[camel(key)] ?? '';
  return v == null ? '' : String(v).trim();
}

function camel(snake) {
  return snake.split('_').map((p, i) => i === 0 ? p : p.charAt(0).toUpperCase() + p.slice(1)).join('');
}

/**
 * Resolve variables in `text` for the given `contact`.
 * Returns { text, missing } where `missing` is the list of variable names
 * that had no value and no fallback.
 */
export function resolveVariables(text, contact) {
  if (!text) return { text: text || '', missing: [] };
  const missing = [];
  const resolved = text.replace(VAR_RE, (_m, name, fallback) => {
    const v = getField(contact, name);
    if (v) return v;
    if (fallback !== undefined) return fallback;
    if (!missing.includes(name)) missing.push(name);
    return `[missing:${name}]`;
  });
  return { text: resolved, missing };
}

export function personalize(text, contact) {
  return resolveVariables(text, contact).text;
}

export const AVAILABLE_VARS = [
  { key: 'first_name', label: 'First name',  suggestedFallback: 'there' },
  { key: 'last_name',  label: 'Last name',   suggestedFallback: '' },
  { key: 'company',    label: 'Company',     suggestedFallback: 'your company' },
  { key: 'job_title',  label: 'Job title',   suggestedFallback: 'the role' },
];

// Given a variable name and optional fallback, return the token to insert.
export function buildToken(name, fallback) {
  const fb = (fallback || '').trim();
  return fb ? `{{${name}|${fb}}}` : `{{${name}}}`;
}

// Extract all variables + fallbacks from a text.
export function extractVariables(text) {
  if (!text) return [];
  const out = [];
  const seen = new Set();
  const re = new RegExp(VAR_RE.source, 'g');
  let m;
  while ((m = re.exec(text)) !== null) {
    const name = m[1];
    const fallback = m[2] !== undefined ? m[2] : null;
    const key = `${name}|${fallback || ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ name, fallback, raw: m[0], index: m.index });
  }
  return out;
}

// Replace the FIRST occurrence of a specific raw token in text with a new one.
export function replaceToken(text, oldRaw, newToken) {
  const i = text.indexOf(oldRaw);
  if (i < 0) return text;
  return text.slice(0, i) + newToken + text.slice(i + oldRaw.length);
}

// Replace ALL occurrences of a variable (by name) with a new token.
export function replaceAllByName(text, name, newToken) {
  const re = new RegExp('\\{{1,2}' + name + '(?:[:|][^{}]*)?\\}{1,2}', 'g');
  return text.replace(re, newToken);
}
